import { useMemo, useState } from 'react';
import { Link, useLocation, matchPath } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, ChevronDown, ChevronUp, Loader2, Sparkles, X } from 'lucide-react';
import { tooltipsApi } from '@/api/endpoints/admin';
import { openZendeskHelpCenterSso } from '@/features/support/lib/zendesk-sso';
import { isReleaseFeatureEnabled } from '@/lib/release-flags';
import { useAuthStore } from '@/stores/auth.store';
import { toast } from '@/stores/toast.store';
import { getApiErrorMessage } from '@/api/client';
import { cn } from '@/lib/cn';

const CHECKLIST_TOUR = 'activation.checklist.v1';
const SSI_ROLES = new Set(['SSIAdministrator', 'SSIEmployee']);
/** Property-first checklist does not apply to vault-only users (US-DVU-05/06). */
const HIDDEN_ROLES = new Set([...SSI_ROLES, 'DrawingVaultUser']);

type ChecklistStep = {
  id: string;
  title: string;
  description: string;
  to: string;
};

const STEPS: ChecklistStep[] = [
  {
    id: 'open-properties',
    title: 'Open Properties',
    description: 'Find a building in your portfolio.',
    to: '/properties',
  },
  {
    id: 'open-building',
    title: 'Open a building',
    description: 'Click a row to see measurements and reports.',
    to: '/properties',
  },
  {
    id: 'view-drawings',
    title: 'Visit Drawing Vault',
    description: 'Open drawings for a building when you need CAD or PDFs.',
    to: '/drawing-vault',
  },
  {
    id: 'support-guide',
    title: 'Visit Support',
    description: 'Get help articles and contact options.',
    // DEF-256: the flag-off fallback must not land on the My Guide tab —
    // that empty "no guide published" state is what the defect was about.
    to: '/support?tab=articles',
  },
];

/**
 * Compact getting-started checklist (activation spike Phase 1).
 * Progress reuses tour_progress under tour name activation.checklist.v1
 * (lastStep = highest completed index, completed = all done or dismissed).
 */
export function GettingStartedChecklist() {
  const role = useAuthStore((s) => s.user?.role);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  // FR-79: impersonation swaps `user` to the client, so the SSI role hide
  // is not enough. `realUser` is the operator — hide the checklist then.
  const realUser = useAuthStore((s) => s.realUser);
  const location = useLocation();
  const queryClient = useQueryClient();
  const [collapsed, setCollapsed] = useState(false);
  const [supportOpenPending, setSupportOpenPending] = useState(false);
  const zendeskSupportEnabled = isReleaseFeatureEnabled('support-zendesk');

  // DEF-396 — property detail has its own dense shell; flashing this checklist
  // above it while the building query loads looks like a wrong page. Hide on
  // `/properties/:buildingId` (and nested edit/drawings) entirely.
  const isPropertyDetailRoute = Boolean(
    matchPath({ path: '/properties/:buildingId/*', end: false }, location.pathname) ||
      matchPath({ path: '/properties/:buildingId', end: true }, location.pathname),
  );

  const enabled =
    isAuthenticated
    && Boolean(role)
    && !HIDDEN_ROLES.has(role ?? '')
    && !isPropertyDetailRoute
    && !realUser;

  const { data: progress, isPending: progressPending } = useQuery({
    queryKey: ['activation', 'checklist', CHECKLIST_TOUR],
    queryFn: () => tooltipsApi.tourProgress(CHECKLIST_TOUR),
    enabled,
    staleTime: 5 * 60 * 1000,
  });

  const save = useMutation({
    mutationFn: (next: { completed: boolean; lastStep: number }) =>
      tooltipsApi.setTourProgress(CHECKLIST_TOUR, next),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['activation', 'checklist', CHECKLIST_TOUR],
      });
    },
  });

  const completedCount = useMemo(() => {
    if (!progress) return 0;
    if (progress.completed) return STEPS.length;
    return Math.min(STEPS.length, Math.max(0, progress.lastStep));
  }, [progress]);

  // Wait for progress before painting — otherwise incomplete users see a one-frame
  // flash of the checklist (and completed users flash it before it disappears).
  if (!enabled || progressPending || progress?.completed) return null;

  function handleMarkStep(index: number): void {
    const nextStep = Math.max(completedCount, index + 1);
    const done = nextStep >= STEPS.length;
    save.mutate({ completed: done, lastStep: nextStep });
  }

  function handleDismiss(): void {
    save.mutate({ completed: true, lastStep: completedCount });
  }

  /**
   * DEF-256: the checklist's Support step used to fetch/download a
   * per-role guide PDF that was never published, so it could never
   * complete. Per the Test Lead's 2026-07-31 decision, it now opens
   * the same Zendesk Help Center link as the sidebar's Support entry
   * (Sidebar.tsx#handleSupportZendeskClick) and completes on open.
   * Only called when the `support-zendesk` flag is on — see the
   * step's Link fallback below for the flag-off / native-page path.
   */
  async function handleOpenSupport(index: number): Promise<void> {
    const userId = useAuthStore.getState().user?.userId;
    if (typeof userId !== 'number') {
      toast.error('Could not open Support. Please sign in again.');
      return;
    }
    setSupportOpenPending(true);
    try {
      await openZendeskHelpCenterSso(userId);
      handleMarkStep(index);
    } catch (e: unknown) {
      toast.error(getApiErrorMessage(e, 'Could not open Support. Please try again.'));
    } finally {
      setSupportOpenPending(false);
    }
  }

  return (
    <aside
      className="mb-4 overflow-hidden rounded-xl border border-border bg-card text-card-foreground shadow-sm"
      aria-label="Getting started"
    >
      <div className="h-1 w-full bg-primary/20">
        <div
          className="h-full bg-primary transition-[width] duration-[var(--duration-base)] ease-[var(--ease-out)] motion-reduce:transition-none"
          style={{ width: `${(completedCount / STEPS.length) * 100}%` }}
        />
      </div>

      <div className="flex items-center justify-between gap-3 px-4 py-3">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Sparkles className="h-4 w-4" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold tracking-tight">Getting started</p>
            <p className="text-xs text-muted-foreground">
              {completedCount} of {STEPS.length} complete
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            className="rounded-md p-2 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
            aria-expanded={!collapsed}
            aria-label={collapsed ? 'Expand checklist' : 'Collapse checklist'}
            onClick={() => setCollapsed((c) => !c)}
          >
            {collapsed ? (
              <ChevronDown className="h-4 w-4" aria-hidden="true" />
            ) : (
              <ChevronUp className="h-4 w-4" aria-hidden="true" />
            )}
          </button>
          <button
            type="button"
            className="rounded-md p-2 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
            aria-label="Dismiss getting started"
            onClick={handleDismiss}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </div>

      {!collapsed && (
        <ol className="space-y-1 px-3 pb-3">
          {STEPS.map((step, index) => {
            const isDone = index < completedCount;
            const stepNumber = index + 1;
            return (
              <li
                key={step.id}
                className={cn(
                  'flex items-start gap-3 rounded-lg px-2 py-2.5 transition-colors',
                  !isDone && 'hover:bg-muted/60',
                )}
              >
                <span
                  className={cn(
                    'mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                    isDone
                      ? 'bg-primary text-primary-foreground'
                      : 'border border-border bg-muted text-muted-foreground',
                  )}
                  aria-hidden="true"
                >
                  {isDone ? <CheckCircle2 className="h-3.5 w-3.5" /> : stepNumber}
                </span>
                <div className="min-w-0 flex-1">
                  <p
                    className={cn(
                      'text-sm font-medium',
                      isDone && 'text-muted-foreground line-through',
                    )}
                  >
                    {step.title}
                  </p>
                  <p className="text-xs text-muted-foreground">{step.description}</p>
                  {!isDone &&
                    (step.id === 'support-guide' && zendeskSupportEnabled ? (
                      <button
                        type="button"
                        disabled={supportOpenPending}
                        className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary transition-colors hover:bg-primary/15 disabled:cursor-not-allowed disabled:opacity-60"
                        onClick={() => void handleOpenSupport(index)}
                      >
                        {supportOpenPending ? (
                          <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
                        ) : null}
                        Continue
                      </button>
                    ) : (
                      <Link
                        to={step.to}
                        className="mt-2 inline-flex items-center rounded-md bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary transition-colors hover:bg-primary/15"
                        onClick={() => handleMarkStep(index)}
                      >
                        Continue
                      </Link>
                    ))}
                </div>
              </li>
            );
          })}
        </ol>
      )}

      <div
        className="sr-only"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={STEPS.length}
        aria-valuenow={completedCount}
        aria-label="Getting started progress"
      />
    </aside>
  );
}
